/**
 * Order Status Update Component
 * Public page for production floor - accessible via QR code
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { OrderService } from '../services/orderService';
import type { RollShadeOrder } from '../types/madebb.types';


// Production employee affirmations
const AFFIRMATIONS = [
  "Your craftsmanship makes homes beautiful!",
  "Quality work starts with quality people like you!",
  "Every shade you make brings comfort to a family.",
  "Your attention to detail doesn't go unnoticed.",
  "You're building more than shades - you're building trust.",
  "Great work today! Your skills shine through.",
  "Precision and care - that's your signature!",
  "Another masterpiece in the making!",
  "Your work ethic inspires the whole team.",
  "Excellence is a habit - and you've got it!",
  "Customers love what you create!",
  "Your dedication makes all the difference.",
  "Skilled hands, focused mind, perfect result!",
  "You make it look easy - but we know it's not!",
  "Pride in workmanship - that's you!",
  "Every stitch, every cut, every detail matters. Thank you!",
  "Your expertise is the foundation of our success.",
  "Quality assurance approved - you're amazing!",
  "The best shades come from the best craftspeople.",
  "You're not just meeting standards - you're setting them!",
  "Another day, another perfect shade. Well done!",
  "Your work brings shade and comfort to many.",
  "Consistency, quality, excellence - that's your brand!",
  "The team is lucky to have someone like you.",
  "Making the world more comfortable, one shade at a time!"
];

export const OrderStatusUpdate: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<RollShadeOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [affirmation, setAffirmation] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Extract security token from URL query parameters
  const securityToken = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const fetchedOrder = await OrderService.getOrder(orderId!, securityToken || undefined);
      setOrder(fetchedOrder);
    } catch (err: any) {
      setError(err.message || 'Order not found');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus: 'in_production' | 'shipped') => {
    if (!order || !securityToken) return;

    setUpdating(true);
    setError(null);

    try {
      const action = newStatus === 'in_production' ? 'start' : 'complete';
      const response = await fetch(
        `/api/floor-status/${order.orderId}?token=${encodeURIComponent(securityToken)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action }),
        }
      );
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to update status');
      }

      // Show random affirmation
      const randomAffirmation = AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)];
      setAffirmation(randomAffirmation);
      setShowSuccess(true);

      // Reload order to show updated status
      await loadOrder();
    } catch (err: any) {
      setError(err.message || 'Failed to update order status');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready': return '#3498db';
      case 'in_production': return '#f39c12';
      case 'completed': return '#27ae60';
      case 'wom': return '#e67e22';
      // Legacy status support
      case 'pending': return '#3498db';
      case 'shipped': return '#27ae60';
      default: return '#95a5a6';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ready': return 'Ready to Produce';
      case 'in_production': return 'In Production';
      case 'completed': return 'Completed / Shipped';
      case 'wom': return 'Waiting on Materials';
      // Legacy status support
      case 'pending': return 'Ordered';
      case 'shipped': return 'Shipped';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 2
        }}
      >
        <Paper sx={{ padding: 4, textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error?.includes('Invalid security token') ? 'Access Denied' : 'Order Not Found'}
          </Typography>
          <Typography color="text.secondary">
            {error?.includes('Invalid security token')
              ? 'Invalid security token. Please scan the QR code from the order form.'
              : error || 'The order you are looking for does not exist.'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: { xs: 2, md: 4 }
      }}
    >
      <Container maxWidth="sm">
        {/* Logo */}
        <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
          <img
            src="/madebb/logo.png"
            alt="Made.bb"
            style={{ maxWidth: '250px', width: '100%' }}
          />
        </Box>

        {/* Success/Affirmation Card */}
        {showSuccess && affirmation && (
          <Card
            sx={{
              marginBottom: 3,
              background: 'linear-gradient(135deg, #27ae60, #2ecc71)',
              color: 'white',
              textAlign: 'center'
            }}
          >
            <CardContent>
              <CheckCircleIcon sx={{ fontSize: 60, marginBottom: 2 }} />
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Status Updated!
              </Typography>
              <Typography variant="h6" sx={{ fontStyle: 'italic', marginTop: 2 }}>
                "{affirmation}"
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Order Info */}
        <Paper elevation={8} sx={{ padding: 4, marginBottom: 3 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom textAlign="center">
            {order.orderId}
          </Typography>

          <Box
            sx={{
              padding: 2,
              background: getStatusColor(order.status),
              borderRadius: 2,
              marginBottom: 3,
              textAlign: 'center'
            }}
          >
            <Typography variant="h6" color="white" fontWeight="bold">
              Current Status: {getStatusLabel(order.status)}
            </Typography>
          </Box>

          <Typography variant="body1" gutterBottom>
            <strong>Dimensions:</strong> {order.dimensions.width}" × {order.dimensions.drop}"
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Material:</strong> {order.material.name} - {order.material.color}
          </Typography>
          <Typography variant="body1" gutterBottom>
            <strong>Hardware:</strong> {order.hardwareColor}
          </Typography>
          <Typography variant="body1">
            <strong>Created:</strong> {new Date(order.createdAt).toLocaleDateString()}
          </Typography>
        </Paper>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ marginBottom: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Status Update Buttons */}
        <Paper elevation={8} sx={{ padding: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom textAlign="center">
            Update Status
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={updating || order.status === 'in_production' || order.status === 'completed' || order.status === 'shipped'}
              onClick={() => handleStatusUpdate('in_production')}
              sx={{
                padding: '15px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #f39c12, #e67e22)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #e67e22, #d35400)'
                },
                '&:disabled': {
                  background: '#bdc3c7'
                }
              }}
            >
              {updating ? <CircularProgress size={24} color="inherit" /> : 'Start Production'}
            </Button>

            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={updating || order.status === 'ready' || order.status === 'pending' || order.status === 'completed' || order.status === 'shipped'}
              onClick={() => handleStatusUpdate('shipped')}
              sx={{
                padding: '15px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #27ae60, #2ecc71)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #2ecc71, #27ae60)'
                },
                '&:disabled': {
                  background: '#bdc3c7'
                }
              }}
            >
              {updating ? <CircularProgress size={24} color="inherit" /> : 'Mark as Shipped'}
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginTop: 2, textAlign: 'center' }}>
            Use this page to update the order status during production
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};
